import React, { useState } from "react";
import SOAPForm from "../../applicationUi/components/SOAPForm";
import ProgressSummaryForm from "../../applicationUi/components/ProgressSummaryForm";
import ProgressSummaryList from "../../applicationUi/components/ProgressSummaryList";
import TreatmentPlanForm from "../../applicationUi/components/TreatmentPlanForm";
import TreatmentPlanList from "../../applicationUi/components/TreatmentPlanList";

function FormsPage() {
  const [isSOAPModalOpen, setIsSOAPModalOpen] = useState(false);
  const [isProgressSummaryModalOpen, setIsProgressSummaryModalOpen] = useState(false);
  const [isProgressSummaryListOpen, setIsProgressSummaryListOpen] = useState(false);
  const [isTreatmentPlanModalOpen, setIsTreatmentPlanModalOpen] = useState(false);
  const [isTreatmentPlanListOpen, setIsTreatmentPlanListOpen] = useState(false);

  const handleSaveSOAP = (soapData) => {
    console.log(`[Forms Page] Saved SOAP Data:`, soapData);
    // Handle saving the SOAP data to your backend or state
  };

  const handleSaveProgressSummary = (progressData) => {
    console.log(`[Forms Page] Saved Progress Summary Data:`, progressData);
    // Handle saving the progress summary data to your backend or state
    // TODO: Implement backend integration for progress summary
  };

  const handleSaveTreatmentPlan = (treatmentPlanData) => {
    console.log(`[Forms Page] Saved Treatment Plan Data:`, treatmentPlanData);
    // Treatment plan saves are handled by the TreatmentPlanForm component via Redux thunks
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Clinical Forms</h1>
        <p className="mt-2 text-gray-600">Access and complete various clinical forms and assessments.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* SOAP Form Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">SOAP Form</h3>
              <p className="text-sm text-gray-500">Subjective, Objective, Assessment, Plan</p>
            </div>
          </div>
          <p className="text-gray-600 text-sm mb-4">
            Complete comprehensive SOAP notes for client sessions with structured or freeform format options.
          </p>
          <button
            onClick={() => setIsSOAPModalOpen(true)}
            className="w-full rounded-sm bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
            Open SOAP Form
          </button>
        </div>

        {/* Progress Summary Form Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">Progress Summary Report</h3>
              <p className="text-sm text-gray-500">Speech & Language Progress Tracking</p>
            </div>
          </div>
          <p className="text-gray-600 text-sm mb-4">
            Document client progress, goals, and treatment outcomes for speech and language therapy sessions.
          </p>
          <div className="space-y-2">
            <button
              onClick={() => setIsProgressSummaryModalOpen(true)}
              className="w-full rounded-sm bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600">
              Create New Summary
            </button>
            <button
              onClick={() => setIsProgressSummaryListOpen(true)}
              className="w-full rounded-sm bg-green-100 px-3 py-2 text-sm font-semibold text-green-700 shadow-sm hover:bg-green-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600">
              View All Summaries
            </button>
          </div>
        </div>

        {/* Treatment Plan Form Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">Treatment Plan</h3>
              <p className="text-sm text-gray-500">Create comprehensive treatment plans</p>
            </div>
          </div>
          <p className="text-gray-600 text-sm mb-4">
            Document treatment goals, current communication status, and detailed treatment plans for clients.
          </p>
          <div className="space-y-2">
            <button
              onClick={() => setIsTreatmentPlanModalOpen(true)}
              className="w-full rounded-sm bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600">
              Create Treatment Plan
            </button>
            <button
              onClick={() => setIsTreatmentPlanListOpen(true)}
              className="w-full rounded-sm bg-blue-100 px-3 py-2 text-sm font-semibold text-blue-700 shadow-sm hover:bg-blue-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600">
              View All Treatment Plans
            </button>
          </div>
        </div>
      </div>

      {/* SOAP Form Modal */}
      {isSOAPModalOpen && (
        <SOAPForm
          onSave={handleSaveSOAP}
          onClose={() => setIsSOAPModalOpen(false)}
        />
      )}

      {/* Progress Summary Form Modal */}
      {isProgressSummaryModalOpen && (
        <ProgressSummaryForm
          onSave={handleSaveProgressSummary}
          onClose={() => setIsProgressSummaryModalOpen(false)}
        />
      )}

      {/* Progress Summary List Modal */}
      {isProgressSummaryListOpen && (
        <ProgressSummaryList
          onClose={() => setIsProgressSummaryListOpen(false)}
        />
      )}

      {/* Treatment Plan Form Modal */}
      {isTreatmentPlanModalOpen && (
        <TreatmentPlanForm
          onSave={handleSaveTreatmentPlan}
          onClose={() => setIsTreatmentPlanModalOpen(false)}
        />
      )}

      {/* Treatment Plan List Modal */}
      {isTreatmentPlanListOpen && (
        <TreatmentPlanList
          onClose={() => setIsTreatmentPlanListOpen(false)}
        />
      )}
    </div>
  );
}

export default FormsPage;
